"use client";

import { CcBlogCardGrid } from "@/components/blog/CcBlogCardGrid";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { useSiteLocale } from "@/components/layout/CcLocaleContext";
import type { BlogListCard } from "@/lib/blog-cards";
import { buildBlogHref } from "@/lib/i18n/paths";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { getHomePageCopy } from "@/lib/home-page-copy";
import { useCcSectionReveal } from "@/lib/use-cc-section-reveal";

export type CcBlogPreviewTheme = "light" | "dark";

/** `home` — full-width home shell, 4-up grid, centered See More (matches `/`). */
export type CcBlogPreviewVariant = "home" | "default";

type CcBlogPreviewProps = {
  /** Default `light` (grey band above the dark FAQ closing band). Use `dark` on AI automation service page. */
  theme?: CcBlogPreviewTheme;
  /** Default `default`. Pass `home` on pages that use the home blog band layout without relying on `.cc-main--home`. */
  variant?: CcBlogPreviewVariant;
  posts: BlogListCard[];
  /** Overrides for pages with their own copy (e.g. SEO service page). */
  eyebrow?: string;
  title?: string;
  moreLabel?: string;
  /** Defaults to the blog index for the current locale. */
  moreHref?: string;
  /** Render nothing when there are no posts instead of the sync placeholder. */
  hideWhenEmpty?: boolean;
  /** Preload the first card image. Turn off when the band sits far below the fold. */
  prioritizeFirstImage?: boolean;
};

export function CcBlogPreview({
  theme = "light",
  variant = "default",
  posts,
  eyebrow,
  title,
  moreLabel,
  moreHref,
  hideWhenEmpty = false,
  prioritizeFirstImage = false,
}: CcBlogPreviewProps) {
  const locale = useSiteLocale();
  const blogCopy = getHomePageCopy(locale).blogPreview;
  const resolvedEyebrow = eyebrow ?? blogCopy.eyebrow;
  const resolvedTitle = title ?? blogCopy.title;
  const resolvedMoreLabel = moreLabel ?? blogCopy.moreLabel;
  const { ref, withRevealClassName } = useCcSectionReveal({ staggerChildren: true });
  const isDark = theme === "dark";
  const isHomeLayout = variant === "home";
  const blogHref = moreHref ?? buildBlogHref(locale);
  const displayPosts = posts.slice(0, 4);

  if (hideWhenEmpty && displayPosts.length === 0) {
    return null;
  }

  return (
    <section
      ref={ref}
      className={withRevealClassName(
        [
          "cc-section cc-blog-preview",
          isDark ? "cc-blog-preview--dark" : "cc-blog-preview--light",
          isHomeLayout ? "cc-blog-preview--variant-home" : "",
        ]
          .filter(Boolean)
          .join(" "),
      )}
      id="blog-preview"
      aria-labelledby="blog-preview-title"
      data-cc-blog-surface=""
      data-cc-nav={isDark ? "dark" : "light"}
      {...devSectionAttr("blog-preview")}
    >
      <div className="cc-container cc-blog-preview__container">
        <header className="cc-blog-preview__heading">
          <CcSanjayaEyebrow theme={isDark ? "on-dark" : "on-subtle"}>
            {resolvedEyebrow}
          </CcSanjayaEyebrow>
          <h2 id="blog-preview-title" className="cc-blog-preview__title">
            {resolvedTitle}
          </h2>
        </header>

        {displayPosts.length > 0 ? (
          <>
            <CcBlogCardGrid
              posts={displayPosts}
              idPrefix="blog-preview-post"
              regionLabel={blogCopy.regionLabel}
              imagePriorityUntilIndex={prioritizeFirstImage ? 0 : -1}
            />
            <div className="cc-blog-preview__actions">
              <CcButton
                className="cc-blog-preview__more-btn cc-hero__action cc-hero__action--secondary"
                href={blogHref}
                variant="outline"
                label={resolvedMoreLabel}
                hoverLabel={resolvedMoreLabel}
              />
            </div>
          </>
        ) : (
          <p className="cc-blog-preview__empty">{blogCopy.empty}</p>
        )}
      </div>
    </section>
  );
}
